import { Component, Input, inject, computed, signal, viewChild, TemplateRef, effect } from '@angular/core';
import { MatPaginatorIntl, PageEvent, MatPaginator } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { CardComponent, ExpandListComponent, ExpandListSort, ExpandListConfiguration, ScreenSizeEnum } from 'ok-angular-shared/components';
import { AppState } from 'src/app/app.state';
import { ChargingStationLocation, ChargingStationLocationDisplay } from '../charging-station-location-model';
import { ChargingStationLocationDetailsComponent } from '../charging-station-location-details/charging-station-location-details.component';
import { getPaginatorIntlNoTotal } from '../../../core/logic/paginator-intl';
import { ChargingStationLocationsStateService } from '../charging-station-location-overview/charging-station-locations.state.service';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { FormatAddressPipe } from '../../../shared/pipes/format-address.pipe';
import { TranslateLocationStatusEnum } from 'src/app/shared/pipes/translate-location-status-enum.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChargingStationLocationActionsService } from '../charging-station-location-actions.service';

@Component({
  selector: 'adm-charging-station-location-list',
  templateUrl: './charging-station-location-list.component.html',
  styleUrls: ['./charging-station-location-list.component.scss'],
  imports: [
    CommonModule,
    CardComponent,
    ExpandListComponent,
    MatPaginator,
    ChargingStationLocationDetailsComponent,
    MatTooltip,
    RouterLink,
    FormatAddressPipe,
    TranslateLocationStatusEnum,
  ],
  providers: [{ provide: MatPaginatorIntl, useValue: getPaginatorIntlNoTotal() }],
})
export class ChargingStationLocationListComponent extends PagerBase {
  @Input() chargingStationLocationIdentifier: string | null = null;
  @Input() activeChargingStationLocationIdentifier: string | null = null;
  @Input() selectedPricePolicyId: number | null = null;

  private chargingStationLocationActionsService = inject(ChargingStationLocationActionsService);
  public appState = inject(AppState);
  public state = inject(ChargingStationLocationsStateService);
  public displayColumns!: Array<string>;
  public nameColumnTemplate = viewChild<TemplateRef<unknown>>('nameColumnTemplate');
  public addressColumnTemplate = viewChild<TemplateRef<unknown>>('addressColumnTemplate');
  public organizationColumnTemplate = viewChild<TemplateRef<unknown>>('organizationColumnTemplate');
  public departmentColumnTemplate = viewChild<TemplateRef<unknown>>('departmentColumnTemplate');
  public limitedAccessColumnTemplate = viewChild<TemplateRef<unknown>>('limitedAccessColumnTemplate');
  public appVisibilityStatusColumnTemplate = viewChild<TemplateRef<unknown>>('appVisibilityStatusColumnTemplate');
  public expandList = viewChild.required<ExpandListComponent<ChargingStationLocationDisplay>>('expandList');

  // Sort and pagination for expand list
  public currentSort = signal<ExpandListSort<ChargingStationLocationDisplay>>({ column: 'name', direction: 'asc' });

  // Config for expand list
  public config = computed<ExpandListConfiguration<ChargingStationLocationDisplay>>(() => ({
    classFn: row => (row.dangerBadgeMessage ? 'warning' : ''),
    tooltipFn: row => (row.dangerBadgeMessage ? row.dangerBadgeMessage : ''),
    badgeFn: row => (row.dangerBadgeMessage ? '!' : ''),
    columns: [
      {
        field: 'operatingDate',
        header: '',
        width: '50px',
        disableSort: true,
        sortField: 'operatingDate',
        displayTemplate: this.nameColumnTemplate(),
      },
      { field: 'name', header: 'Navn', width: '2fr', sortField: 'Name' },
      { field: 'address', header: 'Adresse', width: '2fr', sortField: 'road', displayTemplate: this.addressColumnTemplate() },
      {
        field: 'organizationName',
        header: 'Organisation',
        sortField: 'OrganizationName',
        displayTemplate: this.organizationColumnTemplate(),
      },
      {
        field: 'departmentName',
        header: 'Afdeling',
        sortField: 'DepartmentName',
        displayTemplate: this.departmentColumnTemplate(),
      },
      {
        field: 'restricted',
        header: 'Begrænset adgang',
        sortField: 'Restricted',
        visibleFrom: ScreenSizeEnum.md,
        displayTemplate: this.limitedAccessColumnTemplate(),
      },
      {
        field: 'appVisibilityStatus',
        header: 'App ikon',
        sortField: 'AppVisibilityStatus',
        visibleFrom: ScreenSizeEnum.md,
        displayTemplate: this.appVisibilityStatusColumnTemplate(),
      },
    ],
    actions: this.chargingStationLocationActionsService.createLocationActions(locationIdentifier => {
      this.state.updateSingleChargingStationLocation(locationIdentifier);
      this.state.updateLocationDetails.next(locationIdentifier);
    }),
  }));

  constructor() {
    super();
    this.state.$collapseAllLocations
      .pipe(
        tap(() => {
          this.expandList().collapseAll();
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    effect(() => {
      const focus = this.state.focusedChargingStationLocation();
      if (focus) {
        this.expandList().expandRow(focus.identifier);
      }
    });
  }

  public onSortChange(sort: ExpandListSort<ChargingStationLocationDisplay>) {
    this.currentSort.set(sort);

    // Convert ExpandListSort to Material Sort format and update state
    const matSort: Sort = {
      active: sort.column as string,
      direction: sort.direction,
    };

    this.sortData(matSort);
  }

  public pageSwitch(event: PageEvent) {
    this.state.length.set(event.pageSize * (event.pageIndex + 1));
    this.state.updatePage(event);
  }

  public sortData(sort: Sort) {
    this.state.updateSort(sort);
  }

  // Action methods for charging station locations
  public setOperatingDate(location: ChargingStationLocation) {
    this.chargingStationLocationActionsService.setOperatingDate(location, () => {
      this.state.updateSingleChargingStationLocation(location.identifier);
      this.state.updateLocationDetails.next(location.identifier);
    });
  }

  public createOrEditPriceRegulation(location: ChargingStationLocationDisplay) {
    this.chargingStationLocationActionsService.createOrEditPriceRegulation(location, () => {
      this.state.updateLocationDetails.next(location.identifier);
    });
  }

  public editChargingStationLocationDefault(location: ChargingStationLocation) {
    this.chargingStationLocationActionsService.editChargingStationLocationDefault(location, () => {
      this.state.updateSingleChargingStationLocation(location.identifier);
      this.state.updateLocationDetails.next(location.identifier);
    });
  }

  public editChargingStationLocation(location: ChargingStationLocationDisplay) {
    this.chargingStationLocationActionsService.editChargingStationLocation(location, chargingStationLocationIdentifier => {
      if (chargingStationLocationIdentifier) {
        if (location.identifier === chargingStationLocationIdentifier) {
          this.state.updateLocationDetails.next(chargingStationLocationIdentifier);
          this.state.updateSingleChargingStationLocation(chargingStationLocationIdentifier);
        } else {
          this.state.updateSingleChargingStationLocation(chargingStationLocationIdentifier);
        }
      }
    });
  }

  public editPricePolicyForAllConnectors(location: ChargingStationLocation) {
    this.chargingStationLocationActionsService.editPricePolicyForAllConnectors(location, () => {
      this.state.updateSingleChargingStationLocation(location.identifier);
      this.state.updateLocationDetails.next(location.identifier);
    });
  }
}
